















































import { SfButton, SfIcon, SfInput, SfLoader, SkeletonLoader } from '~/components';
import { defineComponent, ref, computed, useContext, watch } from '@nuxtjs/composition-api';
import cartGetters from '~/modules/checkout/getters/cartGetters';
import { useCart } from '~/modules/checkout/composables/useCart';
import { ValidationObserver, ValidationProvider } from "vee-validate";
import {useUiNotification} from "~/composables";

export default defineComponent({
  name: 'CouponCode',
  components: {
    SkeletonLoader,
    SfLoader,
    SfButton,
    SfIcon,
    SfInput,
    ValidationObserver,
    ValidationProvider,
  },
  setup() {
    const { cart, applyCoupon, removeCoupon, error, loading } = useCart();
    const { app } = useContext();
    const { send: sendNotification } = useUiNotification();

    const couponCodeUserInput = ref('');

    const couponsAppliedToCart = computed(() => cartGetters.getAppliedCoupons(cart.value));
    const isCouponCodeApplied = computed(() => !!(couponsAppliedToCart.value.length));
    const hasAnyError = computed(() => Object.values(error.value).some((value) => value !== null));
    const applyCouponMsg = computed(() => {
      const err = error.value.applyCoupon || error.value.removeCoupon

      if (!err || !err.message) {
        return { message: '', name: 'apply-coupon' }
      }

      const marker = 'The following coupon codes could not be applied:'

      if (err.message.includes(marker)) {
        const suffix = err.message.replace(marker, '').trim().replace(/\.+$/, '');

        return {
          ...err,
          message: `${app.i18n.t('Coupon')} ${suffix} ${app.i18n.t('Not valid')}`
        }
      }

      return err
    })

    const applyOrRemoveCoupon = async () => {
      if (!couponsAppliedToCart.value.length && !isInputComplete.value) {
        return;
      }

      const operationPromise = couponsAppliedToCart.value.length
        ? removeCoupon({})
        : applyCoupon({ couponCode: couponCodeUserInput.value.split(',').map(coupon => coupon.trim()) });
      await operationPromise;
      couponCodeUserInput.value = '';
    };

    const couponsAppliedToCartCodes = computed(() => couponsAppliedToCart.value.reduce(
      (acc, cur, i, arr) => acc + cur.code + (i === arr.length - 1 ? '' : ','), ''));

    const isInputComplete = computed(() => {
      return Boolean((couponCodeUserInput.value || '').trim());
    });

    watch([() => error.value.applyCoupon, () => error.value.removeCoupon], ([applyErr, removeErr]) => {
        const err = applyErr || removeErr;

        if (err?.message) {
          let message = err.message;
          const marker = 'The following coupon codes could not be applied:';

          if (message.includes(marker)) {
            let suffix = message
              .replace(marker, '')
              .trim()
              .replace(/\.+$/, '');

            suffix = suffix.replace(/"([^"]*)"/g, '«$1»');
            message = `${app.i18n.t('Coupon')} ${suffix} ${app.i18n.t('Not valid')} ${app.i18n.t('Check the spelling or use another suggestion.')}`;
          }

          sendNotification({
            id: Symbol('error_coupon'),
            message: message,
            type: 'danger',
            icon: 'check',
            persist: false,
            title: app.i18n.tc('Information'),
          });
        }
      },
      { immediate: true }
    );

    return {
      couponCodeUserInput,
      couponsAppliedToCart,
      couponsAppliedToCartCodes,
      isCouponCodeApplied,
      applyCouponMsg,
      removeCoupon,
      applyOrRemoveCoupon,
      hasAnyError,
      isInputComplete,
      loading,
    };
  },
});
